import { EChartsOption } from "echarts";
import {
    applyColors,
    coreServicesLabels,
    DetailsSeries,
    getStyleProp,
    handleInverterPowerValues,
    InverterV2,
    NodeType,
    PowerDetailsSeries,
    PowerSeries,
    toFixedIfNecessary
} from "ngx-core-services";

import { ChartAttributes, LegendInverterTelemetry } from ".";

export const fromPowerDetailsSeriesToPowerSeries = (series: Partial<InverterV2>[], date: string, tz: string) => {
  const day = new Date(date);
  day.setHours(day.getHours() - day.getTimezoneOffset() / 60);
  day.setHours(12 - day.getTimezoneOffset() / 60 - Number(tz));

  const midDay = day.getTime();

  const data = series.reduce((prev: PowerSeries, currentState) => {
    const keys = Object.keys(currentState) as Array<keyof InverterV2>;
    keys.filter((key) => ['TS', '__typename'].includes(key) === false).forEach((key) => {
      const value = handleInverterPowerValues(Number(currentState[key]), key);
      const currentTs = Number(currentState.TS) * 1000;
      const positionValue = Math.floor((currentTs - midDay) / 1000);
      if (prev[key]) {
        prev[key].values.push([positionValue, value]);
      } else {
        prev[key] = { name: key, values: [[positionValue, value]] };
      }
    });
    return prev;
  }, {});

  Object.keys(data).forEach((key) => {
    if (data && data[key].values) {
      data[key].values.sort((a, b) => a[0] <= b[0] ? -1 : 1);
    }
  });

  return data;
};

export const adaptPowersToGraph = (series: Partial<InverterV2>[], date: string, sourceOptions: NodeType[], tz: string): DetailsSeries[] => {
    const data = fromPowerDetailsSeriesToPowerSeries(series, date, tz);
    const s = applyColors(data);

    const powersToGraph: DetailsSeries[] = [];

    if(s.P_B_AGGR?.values?.length > 0 && sourceOptions.includes('battery')){
      powersToGraph.push({
        name: 'P_B_AGGR',
        values: s.P_B_AGGR?.values,
        color: s.P_B_AGGR?.color,
      });
    }

    const hasBP1PositiveValues = s.BP1?.values?.some(([_, value]) => value > 0);
    const hasBP2PositiveValues = s.BP2?.values?.some(([_, value]) => value > 0);
    const hasBPPositiveValues = s.BP?.values?.some(([_, value]) => value > 0);
    if (hasBPPositiveValues && sourceOptions.includes('battery') && !hasBP1PositiveValues && !hasBP2PositiveValues) {
      powersToGraph.push({
        name: 'BP',
        values: s.BP?.values,
        color: s.BP?.color,
      });
    }

    if (hasBP1PositiveValues && sourceOptions.includes('battery')) {
      powersToGraph.push({
        name: 'BP1',
        values: s.BP1?.values,
        color: s.BP1?.color,
      });
    }

    if (hasBP2PositiveValues && sourceOptions.includes('battery')) {
      powersToGraph.push({
        name: 'BP2',
        values: s.BP2?.values,
        color: s.BP2?.color,
      });
    }

    if(s.P_DC_TOT?.values?.length > 0 && sourceOptions.includes('solar')){
      powersToGraph.push({
        name: 'P_DC_TOT',
        values: s.P_DC_TOT?.values,
        color: s.P_DC_TOT?.color,
      });
    }

    if(s.P_EXP?.values?.length > 0 && sourceOptions.includes('grid')){
      powersToGraph.push({
        name: 'P_EXP',
        values: s.P_EXP?.values,
        color: s.P_EXP?.color,
      });
    }

    if(s.P_LOAD?.values?.length > 0 && sourceOptions.includes('home')){
      powersToGraph.push({
        name: 'P_LOAD',
        values: s.P_LOAD?.values,
        color: s.P_LOAD?.color,
      });
    }

    if(s.SOL_IRR?.values?.length > 0 && sourceOptions.includes('irradiance')){
      powersToGraph.push({
        name: 'SOL_IRR',
        values: s.SOL_IRR?.values,
        color: s.SOL_IRR?.color,
      });
    }

    if(s.SOL_TEMP?.values?.length > 0 && sourceOptions.includes('panelTemperature')){
      powersToGraph.push({
        name: 'SOL_TEMP',
        values: s.SOL_TEMP?.values,
        color: s.SOL_TEMP?.color,
      });
    }

    return powersToGraph;
  };

export const appBuildGenericDailyGraph = (series: PowerDetailsSeries[], chartAttributes: ChartAttributes[], labels: Record<string, string>): EChartsOption => {
  /**
   * Filter the series to only include the ones that are in the chartAttributes
   */
  series = series.filter(s => chartAttributes.some(attr => attr.id === s.name));

  /**
   * Filter chartAttributes to only include attributes that have series data (i.e. are selected)
   * This ensures Y-axes are only created for attributes with actual data
   */
  const activeChartAttributes = chartAttributes.filter((attr) => series.some((s) => s.name === attr.id));

  /**
   * Map the attributes to their chartAttributes id/series name
   */
  const attributesMap = new Map(chartAttributes.map(attr => [attr.id, attr]));

  /**
   * Get the yAxis options for each unit (unit on yAxis must be unique)
   * Solarimeter units (W/m², °C) positioning logic:
   * - If there are NO default axes (kW) on the left: first solarimeter axis goes LEFT, second goes RIGHT
   * - If there IS a default axis (kW) on the left: all solarimeter axes go RIGHT
   */
  const SOLARIMETER_UNITS = ['W/m²', '°C'];
  const yAxisOptionsMap = new Map<string, number>();
  const yAxis: any[] = [];

  // Check if there are any non-solarimeter units (default axes like kW)
  const hasDefaultAxis = activeChartAttributes.some(
    (attr) => !SOLARIMETER_UNITS.includes(attr.attribute.graphOpts.unit)
  );

  // Build yAxis - first unit goes LEFT, others go RIGHT
  activeChartAttributes.forEach((attr) => {
    const unit = attr.attribute.graphOpts.unit;
    if (!yAxisOptionsMap.has(unit)) {
      const yAxisIndex = yAxis.length;
      yAxisOptionsMap.set(unit, yAxisIndex);

      const isSolarimeterUnit = SOLARIMETER_UNITS.includes(unit);
      // Solarimeter units go RIGHT only if there's a default axis, otherwise first goes LEFT
      const position = yAxisIndex === 0 ? 'left' : 'right';
      const shouldGoRight = isSolarimeterUnit && hasDefaultAxis;

      yAxis.push({
        type: 'value',
        name: unit,
        nameLocation: 'end',
        position: shouldGoRight ? 'right' : position,
        offset: 0,
        nameTextStyle: {
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
        },
        splitLine: { show: false },
        axisLine: { show: yAxisIndex > 0 || shouldGoRight, onZero: false },
        axisTick: { show: false },
        axisLabel: {
          verticalAlign: 'bottom',
          showMinLabel: false,
          showMaxLabel: false,
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
          formatter: (value: any) => toFixedIfNecessary(value, 2).toString(),
        },
      });
    }
  });

  // Calculate offsets for right-side axes
  const axisStep = 40;
  let rightCol = 0;
  yAxis.forEach((ax: any) => {
    if (ax.position === 'right') {
      ax.offset = rightCol * axisStep;
      rightCol++;
    }
  });

  // dataZoom included by default — consumers that don't need it override with dataZoom: []
  // bottom: 52 accounts for the slider (32px height + 4px bottom + padding); reset to 10 without dataZoom
  return {
    grid: {
      left: 30,
      right: 50,
      bottom: 52,
      containLabel: true
    },
    dataZoom: buildDataZoomSlider(),
    legend: { show: false },
    tooltip: {
      confine: true,
      trigger: 'axis',
      formatter: (elements: any): string => {
        const { data: [valuex] } = elements[0];
        const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
        const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);

        const headerTemplate = elements.length > 0 ? `
          <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
            <div class="tw-font-sm ml-auto">${hour}:${minutes <= 9 ? ('0' + minutes) : minutes} <span class="tw-text-xs tw-font-bold">${hour < 12 ? 'AM' : 'PM'}</span></div>
          </div>
        ` : '';

        const detailsTemplate = elements.map((element: any) => {
          const { seriesName, data: [_, valuey], color } = element;
          const attr = attributesMap.get(seriesName);
          const useSignLabel = attr?.attribute.graphOpts.sign;
          const name = useSignLabel
            ? valuey > 0
              ? useSignLabel.positive
              : useSignLabel.negative
            : seriesName;
          const unit = attr?.attribute.graphOpts.unit || '';
          const value = useSignLabel ? Math.abs(valuey) : valuey;
          return `
          <div
            class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
            <div class="tw-font-medium text-style-italic mr-5 fx-row">
              <div class="s-4 rounded mr-3" style="background: ${color}"></div>
              ${labels[name]}
            </div>
            <div class="tw-font-normal">${value} <span class="tw-text-xs tw-font-bold">${unit}</span></div>
          </div>`;
        }).join('');

        return `${headerTemplate}${detailsTemplate}`;
      },

      backgroundColor: getStyleProp('--white-100'),
      borderColor: getStyleProp('--grey-300'),
      shadowBlur: 0,
      shadowOffsetX: 0,
      shadowOffsetY: 0,
      borderRadius: 4,
      padding: 0,
      textStyle: { color: getStyleProp('--white') },
    },
    xAxis: {
      max: 12 * 60 * 60,
      min: -12 * 60 * 60,
      maxInterval: 12 * 60 * 60 * 2,
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        color: getStyleProp('--grey-300'),
        fontWeight: 'bold',
        inside: true,
        showMinLabel: false,
        showMaxLabel: false,
        hideOverlap: true,
        formatter: (value: number) => {
          const hour = Math.round(value / 3600) + 12;
          return hour === 12 ? 'Noon' : `${hour < 10 ? '0' : ''}${hour}`;
        },
      },
    },
    yAxis,
    series: series.map(s => {
      const attr = attributesMap.get(s.name);
      const color = attr?.attribute.graphOpts.color;
      const yAxisIndex = yAxisOptionsMap.get(attr?.attribute.graphOpts.unit || '') || 0;
      const transformUnitFn = attr?.attribute.graphOpts.transformUnitFn;
      const noArea = attr?.attribute.graphOpts.noArea;
      return ({
        name: s.name,
        type: 'line',
        smooth: true,
        color,
        symbol: 'none',
        yAxisIndex,
        xAxisIndex: 0,
        areaStyle: noArea === true ? undefined : {
          color,
          opacity: 0.3,
        },
        data: s.values.map(([time, value]) => [time, transformUnitFn ? transformUnitFn(value) : value]),
      })
    }),
  };
};

export const formatTime = (value: number) => {
  const totalSeconds = value + 12 * 3600;
  const h = Math.floor(totalSeconds / 3600) % 24;
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
};

export const buildDataZoomSlider = () => [
  {
    type: 'slider' as const,
    start: 0,
    end: 100,
    height: 32,
    bottom: 4,
    borderColor: getStyleProp('--grey-300'),
    fillerColor: 'rgba(3, 159, 206, 0.08)',
    handleStyle: {
      color: getStyleProp('--accent-500'),
      borderColor: getStyleProp('--accent-700'),
    },
    moveHandleStyle: {
      color: getStyleProp('--accent-500'),
    },
    dataBackground: {
      lineStyle: { color: getStyleProp('--grey-400'), width: 1 },
      areaStyle: { color: getStyleProp('--grey-300'), opacity: 0.4 },
    },
    selectedDataBackground: {
      lineStyle: { color: getStyleProp('--accent-500'), width: 1 },
      areaStyle: { color: getStyleProp('--accent-300'), opacity: 0.4 },
    },
    textStyle: { color: getStyleProp('--grey-500') },
    labelFormatter: formatTime,
  },
];

export const updateSelectedAndAxisLimits = (option: EChartsOption, selected: LegendInverterTelemetry) => {
  const updatedOptions: EChartsOption = {
    ...option,
    legend : {
      ...option.legend,
      selected,
    },
  };
  return updatedOptions;
};
